import { isFeatureEnabled } from 'owa-feature-flags';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { getQueryStringParameter, hasQueryStringParameter } from 'owa-querystring';
import { getOrigin } from 'owa-url';
import { getAnchorMailboxHeader, getAnchorMailboxHeaderForExplicitLogon } from './anchormailbox';
import { getOwaCanaryCookie } from 'owa-service/lib/canary';
import getRequestNumber from 'owa-service/lib/getRequestNumber';
import throttledFetch from 'owa-service/lib/throttledFetch';
import { getAccessTokenforResourceAsLazy } from 'owa-tokenprovider';
import { trace } from 'owa-trace';
import { getUserConfiguration } from 'owa-session-store';
import {
    getUrlWithAddedQueryParameters,
    getExplicitPath,
    joinPath,
    ensureLeadingSlash,
} from 'owa-url';
import {
    getCorrelationVector,
    correlationVectorHeaderName,
} from 'owa-service/lib/correlationVector';
import { DatapointOptions } from 'owa-service/lib/RequestOptions';
import { OwsRequestOptions } from './OwsRequestOptions';
import { callResponseCallbacks } from './registerCreateServiceResponseCallback';
import { owsServerParam, owsApiParam, HttpMethod, PrimeServiceError } from './constants';

const MAX_HTTP_HEADER_LENGTH = 2048;
var owsServerParamOverrideAllowed: boolean | null = null;

export async function makeRequest(
    requestUrl: string,
    requestMethod: HttpMethod,
    options: OwsRequestOptions,
    actionName?: string
): Promise<any | Response>;
export async function makeRequest(
    requestUrl: string,
    requestMethod: HttpMethod,
    returnFullResponse?: boolean,
    actionName?: string,
    correlationId?: string,
    requestObject?: any,
    customHeaders?: { [header: string]: any },
    throwServiceError?: boolean,
    includeCredentials?: boolean
): Promise<any | Response>;
export async function makeRequest(
    requestUrl: string,
    requestMethod: HttpMethod,
    optionsOrReturnFullResponse?: boolean | OwsRequestOptions,
    actionName?: string,
    correlationId?: string,
    requestObject?: any,
    customHeaders?: { [header: string]: any },
    throwServiceError?: boolean,
    includeCredentials?: boolean
): Promise<any | Response> {
    // Create the custom headers with lower case headers as we should be
    // case insensitive when checking if header exists
    const customHeadersToUse =
        (typeof optionsOrReturnFullResponse === 'object' &&
            optionsOrReturnFullResponse.customHeaders) ||
        customHeaders;
    const lowerCasedCustomHeaders = customHeadersToUse
        ? Object.keys(customHeadersToUse).reduce<{ [header: string]: any }>(
              (lowerCaseHeaders, headerName) => {
                  lowerCaseHeaders[headerName.toLowerCase()] = customHeadersToUse[headerName];
                  return lowerCaseHeaders;
              },
              {}
          )
        : undefined;

    let options =
        typeof optionsOrReturnFullResponse === 'object'
            ? { ...optionsOrReturnFullResponse, customHeaders: lowerCasedCustomHeaders }
            : {
                  returnFullResponse: optionsOrReturnFullResponse,
                  correlationId,
                  requestObject,
                  customHeaders: lowerCasedCustomHeaders,
                  throwServiceError,
                  includeCredentials,
              };
    let authToken;
    const AuthorizationHeader = 'authorization';
    if (!options.customHeaders || !options.customHeaders[AuthorizationHeader]) {
        let resourceUrl = getOrigin();
        let [token, tokenPromise] = getAccessTokenforResourceAsLazy(resourceUrl);

        // If token is not returned synchronously, we need to await on the tokenPromise
        if (!token) {
            token = await tokenPromise;
        }

        authToken = token;
        options.includeCredentials = !isFeatureEnabled('auth-authzHeaderNoCookies');
    }

    return makeRequestInternal(requestUrl, requestMethod, authToken, options, actionName);
}

async function makeRequestInternal(
    requestUrl: string,
    requestMethod: HttpMethod,
    accessToken: string | undefined,
    options: OwsRequestOptions,
    actionName?: string
): Promise<JSON | Response> {
    const {
        returnFullResponse,
        correlationId,
        requestObject,
        customHeaders,
        throwServiceError,
        sendPayloadAsBody,
        isThrottled,
        includeCredentials,
    } = options;
    let pathParts = /\/(.+?)\//.exec((window && window.location && window.location.pathname) || '');
    let appName = (pathParts && pathParts.length > 0 && ` ${pathParts[1]}`) || '';
    let headers = new Headers();
    headers.append('X-OWA-CANARY', getOwaCanaryCookie());
    headers.append('X-MS-AppName', 'owa-react' + appName);

    const additionalQueryStringParams: { [key: string]: string } = {
        n: getRequestNumber().toString(),
    };
    if (correlationId) {
        headers.append('client-request-id', correlationId);
        additionalQueryStringParams.cri = correlationId;
    }

    if (customHeaders) {
        Object.keys(customHeaders).forEach(k => {
            headers.append(k, customHeaders[k]);
        });
    }

    const cv = getCorrelationVector();
    // the cv can be falsy if it gets corrupted, in that case there is no point in sending it
    if (cv) {
        headers.append(correlationVectorHeaderName, cv);
        additionalQueryStringParams.cv = cv;
    }

    if (accessToken) {
        const headerName = 'Authorization';
        if (isFeatureEnabled('fwk-devTools')) {
            var parts = accessToken.split('.');
            if (parts.length != 3 || parts[2].length === 0) {
                // this can be a signed of an unsigned token flowing through
                trace.error(`BearerTokenIncomplete ${parts.length}`);
            }
        }

        headers.append(headerName, `Bearer  ${accessToken}`);
    }

    const AnchorMailboxHeader = 'x-anchormailbox';
    if (!customHeaders || !customHeaders[AnchorMailboxHeader]) {
        let anchorMailbox = getAnchorMailboxHeaderForExplicitLogon();
        if (isFeatureEnabled('fwk-puidBasedRouting') && anchorMailbox.length === 0) {
            let userConfiguration = getUserConfiguration();
            if (userConfiguration && userConfiguration.SessionSettings) {
                let tenant = userConfiguration.SessionSettings.ExternalDirectoryTenantGuid;
                let puid = userConfiguration.SessionSettings.UserPuid;
                if (puid && tenant) {
                    anchorMailbox = `PUID:${puid}@${tenant}`;
                }
            }
        }

        // if we don't have an anchor mailbox, get it from the
        // cookie
        if (anchorMailbox.length === 0) {
            anchorMailbox = getAnchorMailboxHeader();
        }

        if (anchorMailbox && anchorMailbox.length > 0) {
            headers.append('X-AnchorMailbox', anchorMailbox);
        }
    }

    let requestInit: RequestInit = {
        method: requestMethod,
        credentials: includeCredentials !== false ? 'same-origin' : 'omit',
    };

    if (requestObject) {
        headers.append('Content-Type', 'application/json');
        const requestBody = JSON.stringify(requestObject);

        // The empty post request only works when the request is to OWS or OWA.
        // Match ows/ or owa/ to avoid something like "search/api/v1/events?scenario=owa.react"
        const isOwsOrOwaRequest = requestUrl.search(/ows\/|owa\//i) >= 0;

        if (
            isFeatureEnabled('fwk-emptyPost') &&
            isOwsOrOwaRequest &&
            requestMethod == 'POST' &&
            !sendPayloadAsBody
        ) {
            const uriEncodedRequestBody = encodeURIComponent(requestBody);

            // send request body as a header when possible -- this causes
            // significant performance gains
            if (uriEncodedRequestBody.length < MAX_HTTP_HEADER_LENGTH) {
                headers.append('X-OWA-UrlPostData', uriEncodedRequestBody);
            } else {
                requestInit.body = requestBody;
            }
        } else {
            requestInit.body = requestBody;
        }
    }

    requestInit.headers = headers;

    const url = getFetchUrl(requestUrl, additionalQueryStringParams);
    const fetchFunc = isThrottled ? throttledFetch : fetch;
    const promise = fetchFunc(url, requestInit);

    callResponseCallbacks(requestInit, options, promise, actionName, url);

    return promise
        .then(response => {
            if (returnFullResponse) {
                return response;
            }
            if (isSuccessStatusCode(response.status)) {
                return response.text().then(text => (text ? JSON.parse(text) : {}));
            } else {
                throw new Error(
                    `${requestMethod} request to ${requestUrl} failed, http error:${
                        response.status
                    }`
                );
            }
        })
        .catch(error => {
            trace.warn(error);
            if (throwServiceError) {
                const primeError = {
                    stack: error.stack,
                    message: error.message,
                    correlationVector: cv,
                } as PrimeServiceError;
                throw primeError;
            } else {
                return [];
            }
        });
}

function getFetchUrl(requestUrl: string, additionalParams: { [key: string]: string }): string {
    // For Local OWS Prime testing, this way developer can test their OWS changes with react gulp
    var gatewayUrl = isFeatureEnabled('fwk-proxyOwsPrime') ? '/OutlookGateway/' : '/';
    var modifiedRequestUrl = getExplicitUrl(gatewayUrl, requestUrl);
    var fetchUrl = getOrigin() + modifiedRequestUrl;

    if (
        isFeatureEnabled('fwk-devTools') &&
        hasQueryStringParameter(owsServerParam) &&
        (getQueryStringParameter(owsApiParam) == requestUrl ||
            !hasQueryStringParameter(owsApiParam))
    ) {
        if (owsServerParamOverrideAllowed == null) {
            owsServerParamOverrideAllowed = confirm(
                `A query param '${owsServerParam}' is set to redirect some of calls to '${getQueryStringParameter(
                    owsServerParam
                )}'.

Those calls contain PII. If this is not intentional, click 'Cancel' below or remove the queryparam and reload the page.`
            );
        }

        if (owsServerParamOverrideAllowed) {
            var requestUrlSegments = requestUrl.split('/');
            if (requestUrlSegments.length > 0 && requestUrlSegments.shift() == 'ows') {
                requestUrl = requestUrlSegments.join('/');
            }

            fetchUrl = getQueryStringParameter(owsServerParam) + '/' + requestUrl;
        }
    }

    return getUrlWithAddedQueryParameters(fetchUrl, additionalParams);
}

function getExplicitUrl(gatewayUrl: string, requestUrl: string) {
    /* Get url taking into account explicit logon */
    var modifiedRequestUrl = joinPath(gatewayUrl, requestUrl);
    var modifiedRequestUrlSegments = modifiedRequestUrl.split('/');

    if (
        modifiedRequestUrlSegments.length > 0 &&
        (modifiedRequestUrlSegments[0] == 'ows' ||
            modifiedRequestUrlSegments[0] == 'OutlookGateway')
    ) {
        modifiedRequestUrlSegments[0] = getExplicitPath(
            modifiedRequestUrlSegments[0],
            window.location.pathname
        );
        modifiedRequestUrl = modifiedRequestUrlSegments.join('/');
    }

    return ensureLeadingSlash(modifiedRequestUrl);
}

export function makePostRequest(
    requestUrl: string,
    requestObject: any,
    correlationId?: string,
    returnFullResponse?: boolean,
    customHeaders?: any,
    throwServiceError?: boolean,
    sendPayloadAsBody?: boolean,
    includeCredentials?: boolean,
    actionName?: string,
    datapoint?: DatapointOptions
): Promise<any> {
    const options: OwsRequestOptions = {
        returnFullResponse: returnFullResponse,
        correlationId: correlationId,
        requestObject: requestObject,
        customHeaders: customHeaders,
        throwServiceError: throwServiceError,
        sendPayloadAsBody: sendPayloadAsBody,
        includeCredentials: includeCredentials,
        datapoint,
    };

    return makeRequest(requestUrl, 'POST', options, actionName);
}

export function makePatchRequest(
    requestUrl: string,
    requestObject: any,
    correlationId?: string,
    returnFullResponse?: boolean,
    customHeaders?: any,
    throwServiceError?: boolean,
    isThrottled?: boolean
): Promise<any> {
    const options: OwsRequestOptions = {
        returnFullResponse: returnFullResponse,
        correlationId: correlationId,
        requestObject: requestObject,
        customHeaders: customHeaders,
        throwServiceError: throwServiceError,
        isThrottled: isThrottled,
    };

    return makeRequest(requestUrl, 'PATCH', options);
}

export function makePutRequest(
    requestUrl: string,
    requestObject: any,
    correlationId?: string,
    returnFullResponse?: boolean,
    customHeaders?: any,
    throwServiceError?: boolean,
    actionName?: string
): Promise<any> {
    return makeRequest(
        requestUrl,
        'PUT',
        returnFullResponse,
        actionName,
        correlationId,
        requestObject,
        customHeaders,
        throwServiceError
    );
}

export function makeGetRequest(
    requestUrl: string,
    correlationId?: string,
    returnFullResponse?: boolean,
    customHeaders?: any,
    throwServiceError?: boolean,
    includeCredentials?: boolean,
    actionName?: string
): Promise<any> {
    return makeRequest(
        requestUrl,
        'GET',
        returnFullResponse,
        actionName,
        correlationId,
        undefined /* requestObject */,
        customHeaders,
        throwServiceError,
        includeCredentials
    );
}

export function makeDeleteRequest(
    requestUrl: string,
    requestObject?: any,
    correlationId?: string,
    returnFullResponse?: boolean,
    customHeaders?: any,
    throwServiceError?: boolean,
    isThrottled?: boolean
): Promise<any> {
    const options: OwsRequestOptions = {
        returnFullResponse: returnFullResponse,
        correlationId: correlationId,
        requestObject: requestObject,
        customHeaders: customHeaders,
        throwServiceError: throwServiceError,
        isThrottled: isThrottled,
    };

    return makeRequest(requestUrl, 'DELETE', options);
}
